import { CLASSES } from "../content/classes";
import {
  addShield,
  applyDamage,
  applyStatus,
  type DamageOpts,
  type HealOpts,
} from "./combat";
import {
  alliesOf,
  hasFaithShield,
  hasStatus,
  isFrontal,
  nearby,
  nearbyEnchantCount,
  nearSpirit,
  unseenFor,
} from "./query";
import type { DamageType, StatusId, StatusInst, Unit, World } from "./types";
import { dist } from "./vec";

export interface OutgoingMod {
  amount: number;
  crit: boolean;
}

export interface IncomingMod {
  amount: number;
  blocked: boolean;
}

export interface DamageInfo {
  requested: number;
  dealt: number;
  toShield: number;
  toHp: number;
  crit: boolean;
  type: DamageType;
  tick: boolean;
  echo: boolean;
  skillId?: string;
}

export interface HealConvert {
  amount: number;
  damage: boolean;
}

const NEAR = 4.2;

function eachPassive(unit: Unit): string[] {
  return CLASSES[unit.classId]?.passives ?? [];
}

function livingActors(world: World): Unit[] {
  return world.units.filter((u) => !u.dead || (u.kind === "corpse" && u.state.selfCorpse === 1));
}

export function modifyOutgoing(
  world: World,
  source: Unit,
  target: Unit,
  amount: number,
  _type: DamageType,
  opts: DamageOpts,
): OutgoingMod {
  let dmg = amount;
  let crit = !!opts.guaranteedCrit;
  if (opts.tick || opts.echo) return { amount: dmg, crit };

  for (const id of eachPassive(source)) {
    if (id === "marksmanship") {
      if (source.stillFor >= 0.12) dmg += 1;
      if (target.stillFor >= 0.12 || hasStatus(target, "stun") || hasStatus(target, "stagger")) crit = true;
    }
    if (id === "mysticism") {
      const n = nearbyEnchantCount(world, source, NEAR);
      if (n >= 8) dmg += 2;
      else if (n >= 3) dmg += 1;
    }
    if (id === "spirit_arms" && hasStatus(source, "spirit_arms") && nearSpirit(world, source)) dmg += 1;
    if (id === "ambush" && unseenFor(world, target, source) >= 1) crit = true;
    if (id === "ambush" && (target.ambushGrace[source.id] ?? 0) > 0) crit = true;
  }
  if (hasStatus(source, "avatar") || hasStatus(source, "mystic_sweep") || hasStatus(source, "rally")) dmg += 1;
  if (hasStatus(target, "retribution") && source.team !== target.team) {
    /* bonus is on the spear via bonusIf; vindication handled after */
  }
  return { amount: dmg, crit };
}

export function modifyIncoming(
  world: World,
  target: Unit,
  source: Unit | null,
  amount: number,
  type: DamageType,
  opts: DamageOpts,
): IncomingMod {
  let dmg = amount;
  if (opts.tick) {
    if (type === "psychic") dmg = Math.max(0, dmg - psychicResist(target, type));
    else {
      if (target.classId === "warrior") dmg = Math.max(0, dmg - 1);
      if (target.classId === "paladin") {
        dmg = Math.max(0, dmg - 1);
        if (target.shields.some((s) => s.amount > 0 && s.kind === "any")) dmg = Math.max(0, dmg - 1);
      }
    }
    return { amount: dmg, blocked: false };
  }

  for (const id of eachPassive(target)) {
    if (id === "shields_up") {
      let red = 1;
      if (source && isFrontal(target, source) && world.time - target.lastAttackAt > 1) red = 3;
      dmg = Math.max(0, dmg - red);
    }
    if (id === "sacred_bulwark") {
      dmg = Math.max(0, dmg - 1);
      if (target.shields.some((s) => s.amount > 0 && s.kind === "any")) dmg = Math.max(0, dmg - 1);
    }
    if (id === "mental_toughness" && type === "psychic") dmg = Math.max(0, dmg - 1);
    if (id === "sword_block") {
      /* consumed in applyDamage */
    }
  }
  if (hasStatus(target, "sanctuary") || hasStatus(target, "rally") || hasStatus(target, "tide") || hasStatus(target, "avatar")) {
    dmg = Math.max(0, dmg - 1);
  }
  if (hasStatus(target, "spirit_arms") && nearSpirit(world, target)) dmg = Math.max(0, dmg - 1);
  if (inZone(world, target, "hallowed") && source && source.team !== target.team) {
    /* enemies on hallowed take the slam already; DR is for allies */
  }
  if (inZone(world, target, "hallowed") && (!source || source.team === target.team || target.team === zoneTeam(world, target, "hallowed"))) {
    if (zoneOwnerTeam(world, target, "hallowed") === target.team) dmg = Math.max(0, dmg - 1);
  }
  dmg = Math.max(0, dmg - psychicResist(target, type));
  void opts;
  return { amount: dmg, blocked: false };
}

function psychicResist(unit: Unit, type?: DamageType): number {
  if (type && type !== "psychic") return 0;
  if (unit.classId === "psychic") return type ? 1 : 0;
  return 0;
}

function inZone(world: World, unit: Unit, kind: string): boolean {
  return world.zones.some((z) => z.kind === kind && dist(z.pos, unit.pos) <= z.radius + unit.radius * 0.2);
}

function zoneOwnerTeam(world: World, unit: Unit, kind: string): number | null {
  const z = world.zones.find((zz) => zz.kind === kind && dist(zz.pos, unit.pos) <= zz.radius + unit.radius * 0.2);
  return z ? z.team : null;
}

function zoneTeam(world: World, unit: Unit, kind: string): number | null {
  return zoneOwnerTeam(world, unit, kind);
}

export function beforeHeal(
  world: World,
  target: Unit,
  source: Unit | null,
  amount: number,
  opts: HealOpts,
): HealConvert {
  if (source && source.classId === "priest" && hasStatus(target, "ire") && !opts.tick) {
    void world;
    return { amount, damage: true };
  }
  return { amount, damage: false };
}

export function afterDamage(world: World, target: Unit, source: Unit | null, info: DamageInfo): void {
  if (world.hookDepth > 6) return;

  if (source && !info.tick) {
    if (source.classId === "warrior") {
      applyStatus(world, source, "fleet", 1.5, source, { silent: true });
      applyStatus(world, source, "frenzy", 1.5, source, { silent: true });
    }
    if (source.classId === "priest" && info.dealt > 0) {
      stripStatus(target, "ire");
    }
    grantIreOnDamage(world, source);
    if (source.classId === "assassin" && (source.state.weapon ?? 0) === 0 && info.dealt > 0) {
      applyStatus(world, source, "fleet", 1.1, source, { silent: true });
    }
    markRetribution(world, source, target, "damage");
    if (info.dealt > 0 && (hasStatus(target, "bleeding") || target.hp <= 2)) {
      frenzyNearbyCultistAllies(world, source);
    }
    if (hasStatus(target, "retribution") && source.team !== target.team) {
      applyStatus(world, source, "regen", 3, source, { silent: false });
    }
  }

  if (!info.tick) {
    if (target.classId === "warrior") {
      applyStatus(world, target, "fleet", 1.5, target, { silent: true });
      applyStatus(world, target, "frenzy", 1.5, target, { silent: true });
    }
    if (hasFaithShield(target) && info.dealt > 0) {
      const extra = Math.ceil(info.dealt / 2);
      addShield(target, extra, { kind: "any", max: 6, tag: "faith", sourceId: target.id });
      for (const u of nearby(world, target, 3.2, true)) applyStatus(world, u, "regen", 3, target);
    }
  }

  shareEmpathyDamage(world, target, source, info);
}

export function afterHeal(world: World, target: Unit, source: Unit | null, _amount: number, opts: HealOpts): void {
  if (source && !opts.tick) markRetribution(world, source, target, "heal");
  void world;
}

export function afterStatus(
  world: World,
  target: Unit,
  id: StatusId,
  source: Unit | null,
  inst: StatusInst,
): void {
  if (world.hookDepth > 6) return;

  if (!inst.copy) {
    const dervishes = livingActors(world).filter((u) => u.classId === "dervish" && !u.dead);
    for (const d of dervishes) {
      if (dist(d.pos, target.pos) > NEAR) continue;
      const statusKind = (["fleet", "frenzy", "regen", "invisible", "spirit_arms", "mystic_sweep", "avatar", "sanctuary", "rally", "tide", "warded", "faith"] as StatusId[]).includes(
        id,
      );
      if (!statusKind) continue;
      for (const u of nearby(world, d, NEAR, true)) {
        if (u.id === target.id) continue;
        applyStatus(world, u, id, inst.remaining, d, { copy: true, silent: true });
      }
    }
  }

  const isCondition = ![
    "regen",
    "fleet",
    "frenzy",
    "invisible",
    "spirit_arms",
    "mystic_sweep",
    "avatar",
    "sanctuary",
    "rally",
    "tide",
    "warded",
    "faith",
  ].includes(id);
  if (isCondition && !inst.copy) {
    for (const u of livingActors(world)) {
      if (u.id === target.id) continue;
      if (!hasEmpathy(u)) continue;
      if (dist(u.pos, target.pos) > NEAR) continue;
      applyStatus(world, u, id, Math.max(0.2, inst.remaining - 1), u, { copy: true, silent: true });
    }
  }
  void source;
}

function hasEmpathy(unit: Unit): boolean {
  return unit.classId === "psychic" || hasStatus(unit, "empathy");
}

function shareEmpathyDamage(world: World, target: Unit, source: Unit | null, info: DamageInfo): void {
  if (info.echo || world.hookDepth > 4) return;
  const amount = Math.max(0, (info.tick ? info.requested : info.dealt) - 1);
  for (const u of livingActors(world)) {
    if (!hasEmpathy(u)) continue;
    if (source && u.id === source.id && !info.tick && info.dealt > 0 && !info.echo) {
      const selfAmt = Math.max(0, info.dealt - 1);
      if (selfAmt > 0) applyDamage(world, u, selfAmt, null, { type: "psychic", echo: true, skillId: "empathy" });
      continue;
    }
    if (u.id === target.id) continue;
    if (dist(u.pos, target.pos) > NEAR) continue;
    if (amount > 0) applyDamage(world, u, amount, null, { type: "psychic", echo: true, tick: info.tick, skillId: "empathy" });
  }
}

function grantIreOnDamage(world: World, source: Unit): void {
  const priests = livingActors(world).filter((u) => u.classId === "priest");
  if (!priests.length) return;
  applyStatus(world, source, "ire", 20, priests[0], { silent: true });
}

function markRetribution(world: World, actor: Unit, target: Unit, kind: "damage" | "heal"): void {
  const paragons = livingActors(world).filter((u) => u.classId === "paragon");
  for (const p of paragons) {
    if (kind === "damage" && target.team === p.team && actor.team !== p.team) {
      applyStatus(world, actor, "retribution", 8, p, { silent: true });
    }
    if (kind === "heal" && target.team !== p.team && actor.team !== p.team) {
      applyStatus(world, actor, "retribution", 8, p, { silent: true });
    }
  }
}

function frenzyNearbyCultistAllies(world: World, striker: Unit): void {
  const cultists = livingActors(world).filter((u) => u.classId === "cultist" && u.team === striker.team);
  if (!cultists.length) return;
  applyStatus(world, striker, "frenzy", 2, striker, { silent: true });
}

function stripStatus(unit: Unit, id: StatusId): void {
  unit.statuses = unit.statuses.filter((s) => s.id !== id);
}

export function tickPassives(world: World, unit: Unit, dt: number): void {
  if (unit.dead && unit.state.selfCorpse !== 1) return;

  for (const id of eachPassive(unit)) {
    if (id === "apply_poison") {
      if (unit.stillFor >= 3) unit.state.poisonCharges = 3;
    }
    if (id === "shield_of_faith") {
      addShield(unit, 0, { kind: "any", max: 3, regen: 0.6, tag: "faith", sourceId: unit.id });
      const faith = unit.shields.find((s) => s.tag === "faith");
      if (faith && faith.amount < 3) faith.amount = Math.min(3, faith.amount + 0.45 * dt);
      if (faith && faith.amount > 0 && !hasStatus(unit, "faith")) {
        applyStatus(world, unit, "faith", 1.2, unit, { silent: true });
      }
    }
    if (id === "mental_toughness") {
      addShield(unit, 0, { kind: "psychic", max: 3, regen: 0.5, tag: "mind", sourceId: unit.id });
      const mind = unit.shields.find((s) => s.tag === "mind");
      if (mind && mind.amount < 3) mind.amount = Math.min(3, mind.amount + 0.4 * dt);
    }
    if (id === "sacred_bulwark") {
      addShield(unit, 0, { kind: "any", max: 4, regen: 0.35, tag: "bulwark", sourceId: unit.id });
      const b = unit.shields.find((s) => s.tag === "bulwark");
      if (b && b.amount < 4) b.amount = Math.min(4, b.amount + 0.35 * dt);
    }
    if (id === "gathering_force") {
      unit.state.charge = Math.min(1, (unit.state.charge ?? 0) + dt * 0.16);
    }
    if (id === "beneath_notice") {
      const inSmoke = inZone(world, unit, "smoke");
      if ((unit.stillFor >= 1 || inSmoke) && !hasStatus(unit, "invisible")) {
        applyStatus(world, unit, "invisible", 8, unit, { silent: true });
      }
      if (inSmoke && !hasStatus(unit, "fleet")) applyStatus(world, unit, "fleet", 0.4, unit, { silent: true });
    }
    if (id === "mysticism") {
      const n = nearbyEnchantCount(world, unit, NEAR);
      if (n >= 5) applyStatus(world, unit, "fleet", 0.4, unit, { silent: true });
      if (n >= 8) applyStatus(world, unit, "frenzy", 0.4, unit, { silent: true });
    }
    if (id === "spirit_world") {
      unit.state.spiritTimer = (unit.state.spiritTimer ?? 0) + dt;
      const spirits = world.units.filter((u) => u.kind === "spirit" && u.ownerId === unit.id && !u.dead);
      if (unit.state.spiritTimer >= 8 && spirits.length < 3) {
        unit.state.spiritTimer = 0;
        unit.state.spawnSpirit = 1;
      }
    }
    if (id === "spirit_equilibrium" && unit.kind === "spirit") {
      const eq = 4;
      if (unit.hp > eq) unit.hp = Math.max(eq, unit.hp - dt * 0.4);
      if (unit.hp < eq) unit.hp = Math.min(eq, unit.hp + dt * 0.35);
    }
    if (id === "insatiable") {
      for (const a of alliesOf(world, unit, true)) {
        if (hasStatus(a, "bleeding") || a.hp <= 2) applyStatus(world, a, "frenzy", 0.6, unit, { silent: true });
      }
    }
    if (id === "spirit_arms" && hasStatus(unit, "spirit_arms") && !nearSpirit(world, unit)) {
      unit.statuses = unit.statuses.filter((s) => s.id !== "spirit_arms");
    }
  }

  if (inZone(world, unit, "smoke") && unit.team !== zoneOwnerTeam(world, unit, "smoke")) {
    applyStatus(world, unit, "blind", 0.4, null, { silent: true });
  }
  if (inZone(world, unit, "hallowed") && zoneOwnerTeam(world, unit, "hallowed") === unit.team) {
    if (!hasStatus(unit, "regen")) applyStatus(world, unit, "regen", 1.2, null, { silent: true });
  }
}
